import { AxiosError, AxiosResponse } from 'axios';
import { axiosInstance } from '../../config/axiosConfig';
import { endPointUrls } from '../constants/endPoints';
import {
    IScheduleDetailsPageResponse,
    IScheduleDetailsResponse,
    IScheduleRequest,
    IUpdateScheduleResponse,
    IScheduleDetailsV2Response,
    InputFieldsRequestPayload,
    InputFieldsResponse,
    IGetScheduleRequest,
    IGetScheduleTaxBreakupRequest,
    IApportionmentResponse,
    IApportionmentClientLobResponse,
    IApportionmentPerLayerRequest,
    IUploadTaxScheduleJsonResponse,    
    IUploadJsonRequest,
    IJurisdictionRequest,
    IGetScheduleStatusRequest,
    IScheduleStatusResponse,
    ILobVariationRequest,
    ILobVariationResponse,
    IApportionmentSaveRequest,
    IApportionmentSaveResponse,
    IClassVariationResponse,
    IClassVariationRequest,
    IUploadDTSchedulePDFResponse,
    IUploadDTSchedulePDFRequest,
} from '../types/schedule.type';
import { ILayerDetailsReq, IScheduleLayersResponse } from '../types/insurerjurisdiction.type';
import { IResultsResponse, IUpdateVariationApportionmentRequest } from '../customSlice/scheduleDetailsSlice';
import { ICurrencyResponse } from '../types/rate.type';

const redirectToUnauthorized = (error: AxiosError) => {
    if (error.response?.status === 403) {
        window.location.replace('/Unauthorized-schedule');
    }

    throw error;
};

const saveScheduleDetails = (reqBody: IScheduleRequest): Promise<IScheduleDetailsResponse> => {
    return axiosInstance
        .patch(endPointUrls.saveScheduleDetails, reqBody)
        .then((res: AxiosResponse<IScheduleDetailsResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const getScheduleStatusDetails = (reqBody: IGetScheduleStatusRequest): Promise<IScheduleStatusResponse> => {
    return axiosInstance
        .patch(endPointUrls.getScheduleStatusDetails, reqBody)
        .then((res: AxiosResponse<IScheduleStatusResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const getScheduleDetails = (reqBody: IGetScheduleRequest): Promise<IScheduleDetailsResponse> => {
    return axiosInstance
        .get(endPointUrls.saveScheduleDetails, { params: reqBody })
        .then((res: AxiosResponse<IScheduleDetailsResponse>) => {
            return res.data;
        })
        .catch(redirectToUnauthorized);
};
const getScheduleDetailsV2 = (reqBody: IGetScheduleRequest): Promise<IScheduleDetailsV2Response> => {
    return axiosInstance
        .get(endPointUrls.saveScheduleDetailsV2, { params: reqBody })
        .then((res: AxiosResponse<IScheduleDetailsV2Response>) => {
            return res.data;
        })
        .catch(redirectToUnauthorized);
};
const getScheduleBreakup = (reqBody: IGetScheduleTaxBreakupRequest): Promise<IScheduleDetailsResponse> => {
    return axiosInstance
        .get(endPointUrls.getScheduleTaxBreakup + reqBody.scheduleId)
        .then((res: AxiosResponse<IScheduleDetailsResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const postTaxSchedules = (    
    name?: string,
    description?: string,
    policyNumber?: string,
    policyType?: string,
    effectiveDate?: string | Date,
    expiryDate?: string | Date,
    apportionmentMethod?: string,
    currency?: string,
    layerCount?: string,
    clientLobFlag?: boolean,
    policyHolderLocation?: string,
    status?: string,
    umr?: string,
    clientLobs?: string[],
    insuranceClassIds?: string[],
    insuredName?: string,
): Promise<IScheduleDetailsPageResponse> => {
    return axiosInstance
        .post(endPointUrls.postTaxSchedules, {            
            name,
            description,
            policyNumber,
            policyType,
            effectiveDate,
            expiryDate,
            apportionmentMethod,
            currency,
            layerCount,
            clientLobFlag,
            policyHolderLocation,
            status,
            umr,
            clientLobs,
            insuranceClassIds,
            insuredName,
        })
        .then((res: AxiosResponse<IScheduleDetailsPageResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const getInputFields = (reqBody: InputFieldsRequestPayload): Promise<InputFieldsResponse> => {
    return axiosInstance
        .post(endPointUrls.getInputFields, reqBody)
        .then((res: AxiosResponse<InputFieldsResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const postScheduleLayers = (
    scheduleId: string,
    apportionmentMethod: string,
    currency: string,
    layerCount: string,
    layerDetails: ILayerDetailsReq[],
): Promise<IScheduleLayersResponse> => {
    return axiosInstance
        .patch(endPointUrls.postScheduleLayers, { scheduleId, apportionmentMethod, currency, layerCount, layerDetails })
        .then((res: AxiosResponse<IScheduleLayersResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};
const updateTaxSchedules = (
    scheduleId: string,
    name?: string,
    description?: string,
    policyNumber?: string,
    policyType?: string,
    effectiveDate?: string | Date,
    expiryDate?: string | Date,
    apportionmentMethod?: string,
    currency?: string,
    layerCount?: string,
    clientLobFlag?: boolean,
    policyHolderLocation?: string,
    status?: string,
    umr?: string,
    clientLobs?: string[],
    insuranceClassIds?: string[],
    insuredName?: string,
): Promise<IUpdateScheduleResponse> => {
    return axiosInstance
        .patch(endPointUrls.updateTaxSchedules, {
            scheduleId,
            name,
            description,
            policyNumber,
            policyType,
            effectiveDate,
            expiryDate,
            apportionmentMethod,
            currency,
            layerCount,
            clientLobFlag,
            policyHolderLocation,
            status,
            umr,
            clientLobs,
            insuranceClassIds,
            insuredName,
        })
        .then((res: AxiosResponse<IUpdateScheduleResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const downloadTaxSchedules = (scheduleId: string): Promise<any> => {
    return axiosInstance
        .get(endPointUrls.downloadTaxSchedules + scheduleId, {
            responseType: 'blob', // important
        })
        .then((res: AxiosResponse<any>) => {
            return res;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const uploadTaxScheduleJson = (data: IUploadJsonRequest): Promise<IUploadTaxScheduleJsonResponse> => {
    const formData = new FormData();
    if (data.file) {
        formData.append('file', data.file);
    }
    Object.keys(data).forEach((key) => {
        if (key !== 'file' && (data as any)[key] !== undefined && (data as any)[key] !== null) {
            formData.append(key, (data as any)[key]);
        }
    });

    return axiosInstance
        .post(endPointUrls.uploadTaxScheduleJson, formData, {
            params: { isScheduleLobType: data.isScheduleLobType },
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((res: AxiosResponse<IUploadTaxScheduleJsonResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const uploadDTSchedulePDF = (data: IUploadDTSchedulePDFRequest): Promise<IUploadDTSchedulePDFResponse> => {
    const formData = new FormData();
    if (data.file) {
        formData.append('file', data.file);
    }
    Object.keys(data).forEach((key) => {
        if (key !== 'file' && (data as any)[key] !== undefined && (data as any)[key] !== null) {
            formData.append(key, (data as any)[key]);
        }
    });

    return axiosInstance
        .post(endPointUrls.uploadDTSchedulePDF, formData, {
            params: { isScheduleLobType: data.isScheduleLobType },
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((res: AxiosResponse<IUploadDTSchedulePDFResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const updateApportionment = (reqBody: IScheduleRequest): Promise<IApportionmentResponse> => {
    return axiosInstance
        .patch(endPointUrls.updateVariationApportionment, reqBody)
        .then((res: AxiosResponse<IApportionmentResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const updateJurisdictionDetails = (reqBody: IJurisdictionRequest): Promise<IApportionmentResponse> => {
    return axiosInstance
        .patch(endPointUrls.updateJurisdictionDetails, reqBody)
        .then((res: AxiosResponse<IApportionmentResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};
const updateApportionmentV2 = (reqBody: IApportionmentSaveRequest): Promise<IApportionmentSaveResponse> => {
    return axiosInstance
        .patch(endPointUrls.saveApportionmentData, reqBody)
        .then((res: AxiosResponse<IApportionmentSaveResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const getApportionmentTaxDetails = (scheduleId: string): Promise<IApportionmentClientLobResponse> => {
    return axiosInstance
        .get(endPointUrls.getApportionmentTaxDetails, { params: { scheduleId: scheduleId } })
        .then((res: AxiosResponse<IApportionmentClientLobResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const updateApportionmentPerLayer = (reqBody: IApportionmentPerLayerRequest): Promise<IApportionmentResponse> => {
    return axiosInstance
        .patch(endPointUrls.updateApportionmentv2, reqBody)
        .then((res: AxiosResponse<IApportionmentResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const updateVariationApportionment = (reqBody: IUpdateVariationApportionmentRequest): Promise<IResultsResponse> => {
    return axiosInstance
        .patch(endPointUrls.updateVariationApportionment, reqBody)
        .then((res: AxiosResponse<IResultsResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};
const getLobVariationData = (reqBody: ILobVariationRequest): Promise<ILobVariationResponse> => {
    return axiosInstance
        .post(endPointUrls.getLobVariationData, reqBody)
        .then((res: AxiosResponse<ILobVariationResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const getClassVariationData = (reqBody: IClassVariationRequest): Promise<IClassVariationResponse> => {
    return axiosInstance
        .post(endPointUrls.getClassVariations, reqBody)
        .then((res: AxiosResponse<IClassVariationResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const getCurrencyList = (): Promise<ICurrencyResponse> => {
    return axiosInstance
        .get(endPointUrls.getCurrencyList)
        .then((res: AxiosResponse<ICurrencyResponse>) => {
            return res.data;
        })
        .catch((error: AxiosError) => {
            throw error;
        });
};

const scheduleDetailsServices = {
    saveScheduleDetails,
    updateVariationApportionment,
    postTaxSchedules,
    getInputFields,
    getScheduleStatusDetails,
    getScheduleDetails,
    getScheduleDetailsV2,
    getScheduleBreakup,
    postScheduleLayers,
    updateTaxSchedules,
    downloadTaxSchedules,
    updateApportionment,
    updateApportionmentV2,
    getApportionmentTaxDetails,
    updateApportionmentPerLayer,
    uploadTaxScheduleJson,
    uploadDTSchedulePDF,
    updateJurisdictionDetails,
    getLobVariationData,
    getClassVariationData,
    getCurrencyList,
};

export default scheduleDetailsServices;
